/**
 * Pure helpers for rendering production events (production-records R2) — shared by the job card's
 * History section and the shop-wide /activity feed, so the two surfaces can't drift on what an
 * event is called or how it reads. Mirrors the PRODUCTION_STATUS_LABEL / garmentStage "one function
 * owns label+tone" idiom in order-detail-lib.
 */
import type { components } from '@/lib/api/types.gen';
import type { GarmentTone } from './order-detail-lib';

export type ProductionEvent = components['schemas']['ProductionEventView'];
export type ActivityEvent = components['schemas']['ActivityEventView'];
export type ProductionEventType = ProductionEvent['type'];

/** The date the event log started recording in prod (R1 deploy). Before this moment production left
 *  no per-action history, so an empty timeline must never read as "nothing ever happened". */
export const EVENT_LOG_EPOCH_LABEL = 'Jul 31, 2026';

const UNDO_TYPES: ReadonlySet<ProductionEventType> = new Set([
  'UNPRESS_COPY',
  'UNPRESS_PLACEMENT',
  'SP_UNPRESS',
  'LINE_REOPENED',
  'BATCH_UNPRINTED',
  // Deleting a sheet reopens its garments and erases their press state — a reversal (R1.1).
  'BATCH_DELETED',
  'UNDO_PRODUCED',
  'UNDO_PACKED',
  'UNDO_ALREADY_PRINTED',
  'UNDO_PULLED_FROM_STOCK',
  'UNDO_SHIPPED_FROM_STOCK',
]);

/** An event that reverses earlier work — the rows the state records erase and only this log keeps. */
export function isUndoEvent(type: ProductionEventType): boolean {
  return UNDO_TYPES.has(type);
}

/**
 * The reprint queue's four events. Grouped rather than folded into UNDO_TYPES because the owner's
 * question about them is "what happened to reprints today", not "what was reversed" — a dismiss is
 * both, and the reprint lens is the one where it matters (a dismiss un-blocks an order with the
 * garment possibly still ruined).
 */
const REPRINT_QUEUE_TYPES: ReadonlySet<ProductionEventType> = new Set([
  'REPRINT_QUEUED',
  'REPRINT_QUEUE_REMOVED',
  'REPRINT_RETIRED',
  'REPRINT_RETURNED',
]);

/**
 * Shipping-station events (P5 S2) — all filed under the `shipped` lens, forward and backward moves
 * alike: the question that lens answers is "what happened at the ship station", and a void or a
 * gate override belongs in that audit, not hidden under `undo`. Deliberately NOT in UNDO_TYPES —
 * that set means production-work reversals and is pinned exactly by its test.
 */
const SHIP_STATION_TYPES: ReadonlySet<ProductionEventType> = new Set([
  'SHIP_VERIFIED',
  'SHIP_VERIFY_RESET',
  'SHIP_GATE_OVERRIDDEN',
  'LABEL_BOUGHT',
  'LABEL_VOIDED',
  'FULFILLMENT_CREATED',
]);

/**
 * Badge tone per event: undos + reprint-created draw the eye (amber), presses are in-flight work
 * (blue), terminal completions are done (green), and the ship moment is deliberately muted — it is
 * Shopify's stamp, not shop-floor work.
 *
 * A queued / dismissed / returned garment is amber for the same reason a reissue is: it is
 * outstanding work, or the removal of a block on work that may still be outstanding. Only
 * `REPRINT_RETIRED` is green — its sheet printed, which is the one happy exit from the queue.
 */
export function eventTone(type: ProductionEventType): GarmentTone {
  if (type === 'REPRINT_RETIRED') return 'done';
  if (isUndoEvent(type) || type === 'REISSUE_CREATED' || REPRINT_QUEUE_TYPES.has(type)) {
    return 'attention';
  }
  // Ship station: the gate bypass, the money reversal, and the re-pack draw the eye; a scan is
  // packing in progress; a bought label / pushed tracking are completions (the default `done`).
  if (type === 'SHIP_GATE_OVERRIDDEN' || type === 'LABEL_VOIDED' || type === 'SHIP_VERIFY_RESET') {
    return 'attention';
  }
  if (type === 'SHIP_VERIFIED') return 'progress';
  if (type === 'PRESS_COPY' || type === 'PRESS_PLACEMENT' || type === 'SP_PRESS') return 'progress';
  if (type === 'ORDER_FULFILLED') return 'muted';
  return 'done';
}

/** 0-based copyIndex → the human "copy N" (only per-copy events carry one). */
function copyLabel(copyIndex?: number): string {
  return copyIndex == null ? '' : ` · copy ${copyIndex + 1}`;
}

/**
 * The human line for one event, composed from the event's own fields — never from current state
 * (this log outlives every undo). Kept to a verb phrase; the surrounding row adds garment/actor/time.
 */
export function eventLabel(
  e: Pick<
    ProductionEvent,
    'type' | 'location' | 'copyIndex' | 'qty' | 'blankSku' | 'itemSlug' | 'reason'
  >,
): string {
  switch (e.type) {
    case 'PRESS_COPY':
      return `Pressed ${e.location ?? ''}${copyLabel(e.copyIndex)}`.trim();
    case 'UNPRESS_COPY':
      return `Un-pressed ${e.location ?? ''}${copyLabel(e.copyIndex)}`.trim();
    case 'PRESS_PLACEMENT':
      return `Pressed ${e.location ?? ''} (all copies)`.trim();
    case 'UNPRESS_PLACEMENT':
      return `Un-pressed ${e.location ?? ''} (all copies)`.trim();
    case 'SP_PRESS':
      return `Pressed ${e.location ?? ''} (screen print)`.trim();
    case 'SP_UNPRESS':
      return `Un-pressed ${e.location ?? ''} (screen print)`.trim();
    case 'LINE_PRINTED':
      return `Marked printed${e.qty ? ` ×${e.qty}` : ''}${e.blankSku ? ` · drew blank ${e.blankSku}` : ''}`;
    case 'LINE_REOPENED':
      return `Re-opened${e.blankSku ? ` · restored blank ${e.blankSku}` : ''}`;
    case 'BATCH_PRINTED': {
      // A reprint close-out draws replacement blanks and flips no lines — its qty means "blanks
      // drawn" (the server stamps the discriminating reason). Otherwise qty = garments this close
      // BULK-settled with no press record; 0/absent = a pure status flip.
      if (e.reason === 'reprint close-out') {
        return `Reprint closed out${e.qty ? ` — drew ${e.qty} replacement blank${e.qty === 1 ? '' : 's'}` : ''}`;
      }
      return `Batch closed out${e.qty ? ` — ${e.qty} garment${e.qty === 1 ? '' : 's'} bulk-settled, no press record` : ''}`;
    }
    case 'BATCH_UNPRINTED':
      return `Batch close-out undone${e.qty ? ` — ${e.qty} reopened` : ''}`;
    case 'BATCH_DELETED':
      return `Sheet deleted${e.qty ? ` — ${e.qty} garment${e.qty === 1 ? '' : 's'} back to open` : ''}${e.reason ? ` · ${e.reason}` : ''}`;
    case 'PRODUCED':
      return `Marked produced${e.blankSku ? ` · drew blank ${e.blankSku}` : ''}`;
    case 'UNDO_PRODUCED':
      return `Produce undone${e.blankSku ? ` · restored blank ${e.blankSku}` : ''}`;
    case 'PACKED':
      return `Packed${e.itemSlug ? ` · drew ${e.itemSlug}` : ''}`;
    case 'UNDO_PACKED':
      return `Pack undone${e.itemSlug ? ` · restored ${e.itemSlug}` : ''}`;
    case 'ALREADY_PRINTED':
      return `Marked already printed (off-system)${e.reason ? ` · ${e.reason}` : ''}`;
    case 'UNDO_ALREADY_PRINTED':
      return 'Off-system mark undone';
    case 'PULLED_FROM_STOCK':
      return 'Pulled from shelf stock';
    case 'UNDO_PULLED_FROM_STOCK':
      return 'Shelf pull undone';
    // Finished-garment shelf (finished-garment-stock v1 PR-4): the pick and its crediting undo.
    // `reason` carries the stockKey (the physical identity drawn) — kept on the EVENT for audit, and
    // deliberately NOT interpolated into the label: it is a 100+ char machine key that would blow out
    // an un-truncated timeline Badge. Read it from the event payload when investigating.
    case 'SHIPPED_FROM_STOCK':
      return `Shipped from the garment shelf${e.qty ? ` · ${e.qty} taken` : ''}`;
    case 'UNDO_SHIPPED_FROM_STOCK':
      return `Returned to production — shelf credited${e.qty ? ` · ${e.qty} back` : ''}`;
    case 'REISSUE_CREATED':
      return `Reprint created${e.reason ? ` · ${e.reason}` : ''}`;
    // The queue's four moments. Each renders the server's own reason verbatim: for a QUEUED row that
    // is "<reason> at <station>", for a REMOVED one the operator's mandatory typed reason — the whole
    // point of gating the dismiss is that the reason survives, so it must reach the reader.
    case 'REPRINT_QUEUED':
      return `Added to the reprint queue${e.reason ? ` · ${e.reason}` : ''}`;
    case 'REPRINT_QUEUE_REMOVED':
      return `Removed from the reprint queue${e.reason ? ` · ${e.reason}` : ''}`;
    case 'REPRINT_RETIRED':
      return 'Reprint sheet printed — left the queue';
    case 'REPRINT_RETURNED':
      return `Back in the reprint queue${e.reason ? ` · ${e.reason}` : ''}`;
    case 'ORDER_FULFILLED':
      return 'Shipped — Shopify marked the order fulfilled';
    // The ship station's six moments (P5 S2). A scan carries copyIndex (which garment); an
    // eye-check carries qty + the tagless reason; the money rows carry the server's own context
    // (carrier/service/cost, refund status, tracking) in `reason`, rendered verbatim.
    case 'SHIP_VERIFIED':
      return e.qty && e.qty > 1
        ? `Verified into the box ×${e.qty}${e.reason ? ` · ${e.reason}` : ''}`
        : `Verified into the box${copyLabel(e.copyIndex)}${e.reason ? ` · ${e.reason}` : ''}`;
    case 'SHIP_VERIFY_RESET':
      return `Pack verification cleared${e.qty ? ` — ${e.qty} row${e.qty === 1 ? '' : 's'} removed` : ''}`;
    case 'SHIP_GATE_OVERRIDDEN':
      return `Ship gate overridden${e.reason ? ` · ${e.reason}` : ''}`;
    case 'LABEL_BOUGHT':
      return `Shipping label bought${e.reason ? ` · ${e.reason}` : ''}`;
    case 'LABEL_VOIDED':
      return `Label voided${e.reason ? ` · ${e.reason}` : ''}`;
    case 'FULFILLMENT_CREATED':
      return `Tracking pushed to Shopify${e.reason ? ` · ${e.reason}` : ''}`;
    default: {
      // A type this build doesn't know (server newer than the bundle) still renders readably.
      const t: string = e.type;
      return t.replaceAll('_', ' ').toLowerCase();
    }
  }
}

/** The /activity type-filter lenses — coarse, review-oriented groups (not the raw 20-value enum). */
export type EventGroup = 'press' | 'done' | 'undo' | 'reprint' | 'shipped';

export function eventGroup(type: ProductionEventType): EventGroup {
  // Before the undo check: a dismiss and a return are both reversals, but the lens someone opens to
  // audit reprints is `reprint`, and filing them under `undo` hides them from it.
  if (type === 'REISSUE_CREATED' || REPRINT_QUEUE_TYPES.has(type)) return 'reprint';
  if (type === 'ORDER_FULFILLED' || SHIP_STATION_TYPES.has(type)) return 'shipped';
  if (isUndoEvent(type)) return 'undo';
  if (type === 'PRESS_COPY' || type === 'PRESS_PLACEMENT' || type === 'SP_PRESS') return 'press';
  return 'done';
}

/** Shop-local calendar day (YYYY-MM-DD) for grouping feed rows under day headers. */
export function eventDayKey(atIso: string): string {
  const d = new Date(atIso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Group an already-newest-first event list into contiguous day buckets (feed rendering). */
export function groupEventsByDay<T extends { at: string }>(
  events: readonly T[],
): Array<{ day: string; events: T[] }> {
  const out: Array<{ day: string; events: T[] }> = [];
  for (const e of events) {
    const day = eventDayKey(e.at);
    const last = out[out.length - 1];
    if (last && last.day === day) last.events.push(e);
    else out.push({ day, events: [e] });
  }
  return out;
}
